import React, { useEffect, useRef, useState } from 'react';
import { MessageSquarePlus, Send, X } from 'lucide-react';
import Modal from './ui/Modal';
import { useToast } from './ToastProvider';
import { useI18n } from '../utils/i18n';
import apiService from '../services/apiService';
import { collectSwDiagnostics } from '../utils/swDiagnostics';

// Feedback-Dialog (#42, Welle 2): Fehler/Wunsch mit Freitext, optionalem
// Screenshot und automatisch mitgeschicktem Kontext (Ansicht, Browser).
// Erreichbar über den Button links neben der Glocke im Header.
// #575: bis zu fünf Screenshots, per Dateiauswahl oder Einfügen (Strg+V).
export const MAX_SCREENSHOTS = 5;

let naechsteBildId = 1;
const vorschauUrl = (datei) => {
  try {
    return typeof URL.createObjectURL === 'function' ? URL.createObjectURL(datei) : null;
  } catch (_) {
    return null;
  }
};
const vorschauFreigeben = (bild) => {
  if (bild.url && typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(bild.url);
};

const FeedbackModal = ({ isOpen, onClose }) => {
  const { t } = useI18n();
  const { pushToast } = useToast();
  const [type, setType] = useState('bug');
  const [message, setMessage] = useState('');
  const [bilder, setBilder] = useState([]);
  const [sending, setSending] = useState(false);
  const bilderRef = useRef(bilder);
  bilderRef.current = bilder;

  // Vorschauen beim Schließen der Seite freigeben (Entfernen gibt sie einzeln frei).
  useEffect(() => () => bilderRef.current.forEach(vorschauFreigeben), []);

  const bilderHinzufuegen = (dateien) => {
    const neu = Array.from(dateien || []).filter((d) => String(d.type || '').startsWith('image/'));
    if (neu.length === 0) return;
    const frei = MAX_SCREENSHOTS - bilderRef.current.length;
    if (neu.length > frei) {
      pushToast({
        message: t('feedback.screenshotLimit', 'Höchstens {{max}} Screenshots je Rückmeldung.', { max: MAX_SCREENSHOTS }),
        type: 'info'
      });
    }
    const uebernommen = neu.slice(0, Math.max(0, frei)).map((datei) => ({
      id: naechsteBildId++,
      datei,
      url: vorschauUrl(datei)
    }));
    if (uebernommen.length > 0) setBilder((vorher) => [...vorher, ...uebernommen]);
  };

  const bildEntfernen = (id) => {
    setBilder((vorher) => {
      const weg = vorher.find((b) => b.id === id);
      if (weg) vorschauFreigeben(weg);
      return vorher.filter((b) => b.id !== id);
    });
  };

  // Eingefügte Bilder übernehmen, eingefügter Text bleibt Sache des Textfelds.
  const handlePaste = (e) => {
    const dateien = Array.from((e.clipboardData && e.clipboardData.files) || []);
    if (dateien.some((d) => String(d.type || '').startsWith('image/'))) {
      e.preventDefault();
      bilderHinzufuegen(dateien);
    }
  };

  const contextUrl = `${window.location.pathname}${window.location.search}`;

  const resetAndClose = () => {
    setType('bug');
    setMessage('');
    bilderRef.current.forEach(vorschauFreigeben);
    setBilder([]);
    onClose();
  };

  const handleSend = async () => {
    if (!message.trim() || sending) return;
    setSending(true);
    try {
      // #256: Service-Worker-Zustand mitschicken. Ohne diese Angaben ließ sich
      // bei Meldungen zum Ladeverhalten nie sagen, ob das Gerät den aktuellen
      // Worker überhaupt angenommen hatte — die Diagnose war nur am Arbeitsplatz
      // möglich und dort meist nicht mehr reproduzierbar.
      const sw = await collectSwDiagnostics();
      await apiService.submitFeedback({
        type,
        message: message.trim(),
        contextUrl,
        contextMeta: {
          userAgent: navigator.userAgent,
          viewport: `${window.innerWidth}x${window.innerHeight}`,
          language: navigator.language,
          sw
        },
        screenshotFiles: bilder.map((b) => b.datei)
      });
      pushToast({ message: t('feedback.thanks', 'Danke! Deine Rückmeldung ist angekommen.'), type: 'success' });
      resetAndClose();
    } catch (e) {
      console.error('Feedback error:', e);
      pushToast({ message: t('feedback.error', 'Feedback konnte nicht gesendet werden.'), type: 'error' });
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={resetAndClose}
      icon={<MessageSquarePlus size={20} />}
      title={t('feedback.title', 'Feedback geben')}
      maxWidth="520px"
      closeDisabled={sending}
      closeLabel={t('common.close', 'Schließen')}
      footer={(
        <>
          <button type="button" className="btn btn-secondary" onClick={resetAndClose} disabled={sending}>
            {t('common.cancel', 'Abbrechen')}
          </button>
          <button type="button" className="btn btn-primary" onClick={handleSend} disabled={sending || !message.trim()}>
            <Send size={16} />
            {sending ? t('feedback.sending', 'Sende…') : t('feedback.send', 'Senden')}
          </button>
        </>
      )}
    >
      <div className="form-body" onPaste={sending ? undefined : handlePaste}>
        <div className="form-group">
          <label>{t('feedback.type', 'Art der Rückmeldung')}</label>
          <div style={{ display: 'flex', gap: 'var(--space-4)' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}>
              <input type="radio" name="feedback-type" value="bug" checked={type === 'bug'} onChange={() => setType('bug')} disabled={sending} />
              {t('feedback.typeBug', 'Fehler')}
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)' }}>
              <input type="radio" name="feedback-type" value="wish" checked={type === 'wish'} onChange={() => setType('wish')} disabled={sending} />
              {t('feedback.typeWish', 'Wunsch')}
            </label>
          </div>
        </div>
        <div className="form-group">
          <label htmlFor="feedback-message">{t('feedback.message', 'Beschreibung')}</label>
          <textarea
            id="feedback-message"
            rows="6"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={t('feedback.messagePlaceholder', 'Was ist passiert bzw. was wünschst du dir? Je konkreter, desto besser.')}
            className="form-textarea"
            disabled={sending}
          />
        </div>
        <div className="form-group">
          <label htmlFor="feedback-screenshot">{t('feedback.screenshots', 'Screenshots (optional)')}</label>
          <input
            id="feedback-screenshot"
            type="file"
            accept="image/*"
            multiple
            onChange={(e) => {
              bilderHinzufuegen(e.target.files);
              // Leeren, damit dieselbe Datei nach dem Entfernen erneut wählbar ist
              e.target.value = '';
            }}
            disabled={sending || bilder.length >= MAX_SCREENSHOTS}
          />
          <small className="form-help">
            {t('feedback.screenshotHint', 'Bis zu {{max}} Bilder, auch per Strg+V einfügen.', { max: MAX_SCREENSHOTS })}
          </small>
          {bilder.length > 0 && (
            <ul className="feedback-bilder">
              {bilder.map((b, i) => (
                <li key={b.id} className="feedback-bild">
                  {b.url ? (
                    <img src={b.url} alt={t('feedback.screenshotNr', 'Screenshot {{nr}}', { nr: i + 1 })} />
                  ) : (
                    <span className="feedback-bild-name">{b.datei.name || t('feedback.screenshotNr', 'Screenshot {{nr}}', { nr: i + 1 })}</span>
                  )}
                  <button
                    type="button"
                    className="feedback-bild-entfernen"
                    onClick={() => bildEntfernen(b.id)}
                    disabled={sending}
                    title={t('feedback.screenshotRemove', 'Screenshot {{nr}} entfernen', { nr: i + 1 })}
                    aria-label={t('feedback.screenshotRemove', 'Screenshot {{nr}} entfernen', { nr: i + 1 })}
                  >
                    <X size={14} />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <small className="form-help">
          {t('feedback.contextHint', 'Mitgeschickt wird automatisch: aktuelle Ansicht ({{url}}), Browser und Zeitpunkt.', { url: contextUrl })}
        </small>
      </div>
    </Modal>
  );
};

export default FeedbackModal;
